import { useState, useEffect } from "react";
import { ArrowRight, ArrowLeft, Plus, Minus, Sparkles } from "lucide-react";
import { collection, getDocs, query, where, orderBy } from "firebase/firestore";
import { db } from "@/lib/firebase/config";
import Button from "@/components/ui/Button";
import { formatCurrency } from "@/lib/utils/formatters";
import type { BookingDraft, SelectedAddon } from "@/types/booking";
import { cn } from "@/lib/utils/cn";

// ─── Addon type (from Firestore) ──────────────────────────────────────────────

interface Addon {
  id: string;
  name: string;
  description: string;
  price: number;
  unit: string;
  active: boolean;
  sortOrder: number;
}

async function fetchAddons(): Promise<Addon[]> {
  const q = query(
    collection(db, "addons"),
    where("active", "==", true),
    orderBy("sortOrder", "asc"),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Addon);
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface StepAddonsProps {
  draft: BookingDraft;
  onNext: (addons: SelectedAddon[]) => void;
  onBack: () => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function StepAddons({ draft, onNext, onBack }: StepAddonsProps) {
  const [addons, setAddons] = useState<Addon[]>([]);
  const [loading, setLoading] = useState(true);

  // quantities keyed by addonId
  const [quantities, setQuantities] = useState<Record<string, number>>(() => {
    // Restore from draft if going back
    const restored: Record<string, number> = {};
    draft.addons.forEach((a) => {
      restored[a.addonId] = a.quantity;
    });
    return restored;
  });

  useEffect(() => {
    fetchAddons()
      .then(setAddons)
      .finally(() => setLoading(false));
  }, []);

  const setQty = (id: string, delta: number, max = 10) => {
    setQuantities((prev) => {
      const next = Math.max(0, Math.min(max, (prev[id] ?? 0) + delta));
      if (next === 0) {
        const { [id]: _removed, ...rest } = prev;
        return rest;
      }
      return { ...prev, [id]: next };
    });
  };

  const addonsTotal = addons.reduce((sum, a) => {
    return sum + (quantities[a.id] ?? 0) * a.price;
  }, 0);

  const handleNext = () => {
    const selected: SelectedAddon[] = addons
      .filter((a) => (quantities[a.id] ?? 0) > 0)
      .map((a) => ({
        addonId: a.id,
        name: a.name,
        price: a.price,
        quantity: quantities[a.id],
        unit: a.unit,
        lineTotal: quantities[a.id] * a.price,
      }));
    onNext(selected);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="font-serif text-2xl text-metro-green-950 mb-1">
          Enhance your stay
        </h2>
        <p className="text-sm text-metro-ink-subtle">
          Optional extras to make your visit more memorable. You can skip this
          step if you prefer.
        </p>
      </div>

      {/* Addon cards */}
      {loading ? (
        <div className="grid gap-4 sm:grid-cols-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="h-32 animate-pulse rounded-xl bg-metro-cream-100"
            />
          ))}
        </div>
      ) : addons.length === 0 ? (
        <div className="flex h-40 items-center justify-center rounded-xl border-2 border-dashed border-gray-200 text-sm text-metro-ink-subtle">
          No add-ons available at this time.
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {addons.map((addon) => {
            const qty = quantities[addon.id] ?? 0;
            const isSelected = qty > 0;

            return (
              <div
                key={addon.id}
                className={cn(
                  "rounded-xl border-2 bg-white p-5 transition",
                  isSelected
                    ? "border-metro-green-800 shadow-card"
                    : "border-gray-100 hover:border-metro-green-200",
                )}
              >
                <div className="mb-3 flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="mb-0.5 flex items-center gap-2">
                      <Sparkles
                        size={14}
                        className="shrink-0 text-metro-gold"
                      />
                      <p className="font-medium text-metro-green-950 text-sm">
                        {addon.name}
                      </p>
                    </div>
                    <p className="text-xs text-metro-ink-muted leading-relaxed">
                      {addon.description}
                    </p>
                  </div>
                  <div className="shrink-0 text-right">
                    <p className="font-serif text-base font-medium text-metro-green-950">
                      {formatCurrency(addon.price)}
                    </p>
                    <p className="text-[11px] text-metro-ink-subtle">
                      {addon.unit}
                    </p>
                  </div>
                </div>

                {/* Quantity stepper */}
                <div className="flex items-center justify-between border-t border-gray-100 pt-3">
                  {isSelected && (
                    <p className="text-xs font-medium text-metro-green-800">
                      +{formatCurrency(qty * addon.price)}
                    </p>
                  )}
                  <div
                    className={cn(
                      "flex items-center gap-3",
                      !isSelected && "ml-auto",
                    )}
                  >
                    <button
                      type="button"
                      onClick={() => setQty(addon.id, -1)}
                      disabled={qty === 0}
                      className="flex h-7 w-7 items-center justify-center rounded-full border border-gray-200 text-metro-ink-muted transition hover:border-metro-green-800 hover:text-metro-green-800 disabled:opacity-30"
                    >
                      <Minus size={12} />
                    </button>
                    <span className="w-4 text-center text-sm font-medium text-metro-green-950">
                      {qty}
                    </span>
                    <button
                      type="button"
                      onClick={() => setQty(addon.id, 1)}
                      className="flex h-7 w-7 items-center justify-center rounded-full border border-gray-200 text-metro-ink-muted transition hover:border-metro-green-800 hover:text-metro-green-800"
                    >
                      <Plus size={12} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Running total */}
      {addonsTotal > 0 && (
        <div className="flex items-center justify-between rounded-xl border border-metro-green-100 bg-metro-green-50 px-5 py-3">
          <p className="text-sm font-medium text-metro-green-950">
            Add-ons total
          </p>
          <p className="font-serif text-lg font-medium text-metro-green-950">
            {formatCurrency(addonsTotal)}
          </p>
        </div>
      )}

      {/* Navigation */}
      <div className="flex justify-between border-t border-gray-100 pt-4">
        <Button
          variant="outline"
          leftIcon={<ArrowLeft size={16} />}
          onClick={onBack}
        >
          Back
        </Button>
        <div className="flex items-center gap-3">
          {addonsTotal === 0 && (
            <button
              type="button"
              onClick={handleNext}
              className="text-sm text-metro-ink-subtle underline-offset-2 hover:underline"
            >
              Skip
            </button>
          )}
          <Button rightIcon={<ArrowRight size={16} />} onClick={handleNext}>
            {addonsTotal > 0 ? "Continue" : "Continue without add-ons"}
          </Button>
        </div>
      </div>
    </div>
  );
}